import { toast } from "sonner";
import CustomButton from "../../components/CustomButton";
import CustomInput from "../../components/CustomInput";
import CustomSelect from "../../components/CustomSelect";
import PageHeader from "../../components/PageHeader";
import SubHeadings from "../../components/SubHeadings";
import { useEffect, useState } from "react";
import FileUpload from "../../components/common/FileUpload";
import { useEditMode } from "../../context/EditModeContext";
import { trashOutline } from "../../asset";
import useConfirmToast from "../../util/hooks/useConfirmToast";
import {
  AdmissionStatus,
  CreateAdmissionPayload,
} from "../../types/admissionType";
import { useNavigate, useParams } from "react-router-dom";
import admissionApis from "../../api/admissionApis";
import {
  popupNavigation,
} from "../../util/functions/UniversalFunctions";
import SubButton from "../../components/SubButtons";
import { useBranchCode } from "../../util/hooks/useBranch";
import AcademicCycleSelect from "../../components/common-input/AcademicCycle";
import { IS_LIVE } from "../../util/config/env";

export default function AdmissionsDetails() {
  const initialFormState: CreateAdmissionPayload & {
    status?: AdmissionStatus;
  } = {
    studentName: "",
    gender: "",
    dateOfBirth: "",
    admissionYear: "",
    academicCycleId: 0,
    motherName: "",
    motherPhone: "",
    motherEmail: "",
    fatherName: "",
    fatherPhone: "",
    fatherEmail: "",
    studentPhoto: "",
    branchCode: "",
    studentCategory: "",
    status: null,
  };

  const [formData, setFormData] = useState<
    CreateAdmissionPayload & { status?: AdmissionStatus; className?: string }
  >(initialFormState);

  const navigate = useNavigate();

  const { isEditing, toggleEdit } = useEditMode();
  const { confirm, modal } = useConfirmToast();

  const { id } = useParams<{ id: string }>();

  const branchCode = useBranchCode();

  useEffect(() => {
    if (!id) return;

    const fetchAdmission = async () => {
      const res = await admissionApis.getAdmissionById(Number(id));

      if (res?.status === 1 && res.data) {
        const admission = res.data;

        setFormData({
          id: admission.id,
          studentName: admission.studentName ?? "",
          gender: admission.gender ?? "",
          dateOfBirth: admission.dateOfBirth ?? "",
          admissionYear: admission.admissionYear ?? "",
          academicCycleId: admission.academicCycleId ?? 0,
          studentPhoto: admission.studentPhoto ?? "",
          placeOfBirth: admission.placeOfBirth ?? "",
          residentialAddress: admission.residentialAddress ?? "",
          motherName: admission.motherName ?? "",
          motherPhone: admission.motherPhone ?? "",
          motherEmail: admission.motherEmail ?? "",
          fatherName: admission.fatherName ?? "",
          fatherPhone: admission.fatherPhone ?? "",
          fatherEmail: admission.fatherEmail ?? "",
          branchCode: admission.branchCode ?? "",
          studentCategory: admission.studentCategory ?? "",
          status: admission.status ?? null,
          className: admission.className ?? "",
        });
      }
    };

    fetchAdmission();
  }, [id]);

  const handleInputChange = (
    event: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value, type } = event.target;

    setFormData((prev) => ({
      ...prev,
      [name]:
        name === "academicCycleId" || name === "classId"
          ? Number(value)
          : value,
    }));
  };

  const validateParentInfo = () => {
    const hasMother = formData.motherName || formData.motherPhone;
    const hasFather = formData.fatherName || formData.fatherPhone;

    return hasMother || hasFather;
  };

  const buildUpdatePayload = (): CreateAdmissionPayload => {
    const { id, ...rest } = formData;

    return {
      ...rest,
      branchCode,
      academicCycleId: Number(formData.academicCycleId),
    };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateParentInfo()) {
      toast.error("Any one parent detail is mandatory");
      return;
    }

    if (!id) return;

    const payload = buildUpdatePayload();

    try {
      await admissionApis.updateAdmission(Number(id), payload);

      toggleEdit();
    } catch (error) {}
  };

  const handleDelete = async (id: number) => {
    const confirmed = await confirm(
      "Are you sure you want to delete this Admission data?",
    );

    if (!confirmed) return;

    try {
      const res = await admissionApis.deleteAdmission(id);

      if (res?.status === 1) {
        navigate("/admissions");
      }
    } catch (error) {
      console.error("Delete failed", error);
    }
  };

  const formatStatus = (status?: AdmissionStatus) => {
    if (!status) return "";

    return status
      .replace("ADMISSION_", "")
      .replaceAll("_", " ")
      .toLowerCase()
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const isFinalStatus =
    formData.status === "ADMISSION_OFFERED" ||
    formData.status === "ADMISSION_REJECTED";

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-4 md:flex-row justify-between">
        <PageHeader title={`ADMISSION DETAILS (#${id})`} showBack />
        <div className="flex justify-end items-end px-7 pb-4 lg:pb-0">
          {!IS_LIVE && (
            <SubButton
              text="delete"
              color="bg-error-600"
              icon={trashOutline}
              onClick={() => handleDelete(Number(formData.id))}
            />
          )}
        </div>
        {modal}
      </div>

      <form className="grid gap-5" onSubmit={handleSubmit}>
        <div className="bg-white px-6 py-5 rounded-lg flex flex-col gap-6">
          <div className="flex justify-between items-center">
            <SubHeadings text="Student Details" />
            <div className="flex justify-end items-end px-7 pb-4 lg:pb-0">
              <button
                type="button"
                onClick={toggleEdit}
                className={`${isEditing ? "bg-error-500" : "bg-warning-600"} py-1 px-5 rounded font-semibold text-[13px] text-white`}
              >
                {isEditing ? "Cancel" : "Edit"}
              </button>
            </div>
          </div>
          <div className="flex flex-col-reverse lg:flex-row gap-7 lg:items-end w-full">
            <div className="grid gap-7 w-full">
              <div className="grid lg:grid-cols-2 gap-7">
                <CustomInput
                  name="studentName"
                  label="Name"
                  type="text"
                  readOnly={!isEditing}
                  placeholder="Enter the student's name"
                  value={formData.studentName}
                  onChange={handleInputChange}
                  inputClassName="w-full"
                  required
                />
                <CustomInput
                  name="dateOfBirth"
                  label="Date of Birth"
                  type="date"
                  readOnly={!isEditing}
                  placeholder="DD/MM/YYYY"
                  value={formData.dateOfBirth}
                  onChange={handleInputChange}
                  inputClassName="w-full"
                  required
                />
              </div>

              <div className="flex gap-7">
                <label className="text-sm font-semibold text-neutral-400 block">
                  Gender
                </label>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2">
                    <CustomInput
                      name="gender"
                      type="radio"
                      value="MALE"
                      readOnly={!isEditing}
                      checked={formData?.gender === "MALE"}
                      onChange={handleInputChange}
                      inputClassName="w-3 h-3"
                    />
                    <span
                      className={`text-sm font-semibold ${
                        formData?.gender === "MALE"
                          ? "text-primary-500"
                          : "text-neutral-300"
                      }`}
                    >
                      Male
                    </span>
                  </label>

                  <label className="flex items-center gap-2">
                    <CustomInput
                      name="gender"
                      type="radio"
                      value="FEMALE"
                      readOnly={!isEditing}
                      checked={formData?.gender === "FEMALE"}
                      onChange={handleInputChange}
                      inputClassName="w-3 h-3"
                    />
                    <span
                      className={`text-sm font-semibold ${
                        formData.gender === "FEMALE"
                          ? "text-primary-500"
                          : "text-neutral-300"
                      }`}
                    >
                      Female
                    </span>
                  </label>
                </div>
              </div>

              <div className="grid lg:grid-cols-2 gap-7">
                <CustomSelect
                  name="admissionYear"
                  label="Admission Year"
                  readOnly={!isEditing}
                  value={formData.admissionYear}
                  options={["2026-27", "2027–28", "2028–29"]}
                  placeholder="Select admission year"
                  onChange={handleInputChange}
                  required
                />
                <AcademicCycleSelect
                  name="academicCycleId"
                  label="Enquired for Academic Cycle"
                  value={formData.academicCycleId}
                  readOnly={!isEditing}
                  placeholder="Select academic cycle"
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="grid lg:grid-cols-2 gap-7">
                {!isFinalStatus ? (
                  <CustomSelect
                    name="status"
                    label="Move to Class"
                    value={formData.status ? formatStatus(formData.status) : ""}
                    readOnly={!isEditing}
                    openAsPopup
                    onClick={() =>
                      navigate(popupNavigation("admissionStatus"), {
                        state: { admissionId: formData.id },
                      })
                    }
                    options={[]}
                    placeholder="Select class/section"
                    onChange={handleInputChange}
                  />
                ) : (
                  <CustomInput
                    onChange={() => {}}
                    name="status"
                    label={
                      formData.status === "ADMISSION_OFFERED"
                        ? "Class"
                        : "Admission Status"
                    }
                    value={
                      formData.status === "ADMISSION_OFFERED"
                        ? formData.className || "Class Assigned"
                        : formatStatus(formData.status)
                    }
                    readOnly
                    inputClassName={`font-semibold ${
                      formData.status === "ADMISSION_REJECTED"
                        ? "text-red-500"
                        : "text-green-600"
                    }`}
                  />
                )}

                <CustomSelect
                  name="studentCategory"
                  label="Student Category"
                  value={formData.studentCategory}
                  readOnly={!isEditing}
                  options={[
                    { label: "General", value: "GENERAL" },
                    { label: "OBC", value: "OBC" },
                    { label: "SC", value: "SC" },
                    { label: "ST", value: "ST" },
                  ]}
                  placeholder="Select student category"
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>
            <div className="max-w-md">
              <FileUpload
                value={formData.studentPhoto}
                onUploadComplete={(url) =>
                  setFormData((prev) => ({ ...prev, studentPhoto: url }))
                }
                className="md:w-52 w-36 aspect-square"
                disabled={!isEditing}
              />
            </div>
          </div>
        </div>

        <div className="bg-white px-6 py-5 rounded-lg flex flex-col gap-6">
          <div className="flex justify-between items-center w-full">
            <SubHeadings text="Parent Details" />
            <span className="text-xs text-error-500 font-medium">
              *Any one parent detail is mandatory
            </span>
          </div>

          <div className="grid lg:grid-cols-2 gap-7">
            <CustomInput
              name="motherName"
              label="Mother's Name"
              type="text"
              readOnly={!isEditing}
              placeholder="Enter mother's name"
              value={formData.motherName}
              onChange={handleInputChange}
              inputClassName="w-full"
            />
            <CustomInput
              name="motherPhone"
              label="Mother's Phone Number"
              type="tel"
              readOnly={!isEditing}
              placeholder="Enter mother's phone number"
              value={formData.motherPhone}
              onChange={handleInputChange}
              inputClassName="w-full"
            />
            <CustomInput
              name="motherEmail"
              label="Mother's Email"
              type="email"
              readOnly={!isEditing}
              placeholder="Enter mother's email"
              value={formData.motherEmail}
              onChange={handleInputChange}
              inputClassName="w-full"
            />
            <CustomInput
              name="fatherName"
              label="Father's Name"
              type="text"
              readOnly={!isEditing}
              placeholder="Enter father's name"
              value={formData.fatherName}
              onChange={handleInputChange}
              inputClassName="w-full"
            />
            <CustomInput
              name="fatherPhone"
              label="Father's Phone Number"
              type="tel"
              readOnly={!isEditing}
              placeholder="Enter father's phone number"
              value={formData.fatherPhone}
              onChange={handleInputChange}
              inputClassName="w-full"
            />
            <CustomInput
              name="fatherEmail"
              label="Father's Email"
              type="email"
              readOnly={!isEditing}
              placeholder="Enter father's email"
              value={formData.fatherEmail}
              onChange={handleInputChange}
              inputClassName="w-full"
            />
          </div>
        </div>

        <div className="flex flex-col gap-3 md:flex-row justify-between md:items-center mx-6">
          <span className="text-xs max-w-xl text-error-500 font-medium">
            *Please note that all other student related information can be
            modified directly in the student’s profile, once the students is
            moved to the specific classroom.
          </span>
          <CustomButton
            color="bg-primary-500"
            disabled={!isEditing}
            text="Submit"
            type="submit"
          />
        </div>
      </form>
    </div>
  );
}
